import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { XMarkIcon, TruckIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { Bus } from '../../types';
import { FormField, Input, Select, Textarea, FormCard, DateInput } from '../../components/Forms';

interface BusFormProps {
  bus?: Bus;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (bus: Bus) => void;
}

interface BusFormData {
  busNumber: string;
  plateNumber: string;
  capacity: number;
  model: {
    brand: string;
    model: string;
    year: number;
    color: string;
  };
  driver: {
    name: string;
    phone: string;
    licenseNumber: string;
    licenseExpiry: string;
    experience: number;
    idNumber: string;
  };
  supervisor: {
    name: string;
    phone: string;
    idNumber: string;
  };
  features: string[];
  routes: string[];
  pricing: {
    costPerKM: number;
    dailyRate: number;
    fuelConsumption: number;
  };
  maintenance: {
    mileage: number;
    serviceInterval: number;
    notes: string;
  };
  status: 'available' | 'on-trip' | 'maintenance' | 'out-of-service';
  isActive: boolean;
}

const BusForm: React.FC<BusFormProps> = ({ bus, isOpen, onClose, onSuccess }) => {
  const isEditing = !!bus;
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<BusFormData>({
    busNumber: '',
    plateNumber: '',
    capacity: 45,
    model: {
      brand: '',
      model: '',
      year: new Date().getFullYear(),
      color: ''
    },
    driver: {
      name: '',
      phone: '',
      licenseNumber: '',
      licenseExpiry: '',
      experience: 0,
      idNumber: ''
    },
    supervisor: {
      name: '',
      phone: '',
      idNumber: ''
    },
    features: [],
    routes: [],
    pricing: {
      costPerKM: 2,
      dailyRate: 500,
      fuelConsumption: 8
    },
    maintenance: {
      mileage: 0,
      serviceInterval: 10000,
      notes: ''
    },
    status: 'available',
    isActive: true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data when editing
  useEffect(() => {
    if (isEditing && bus) {
      setFormData({
        busNumber: bus.busNumber,
        plateNumber: bus.plateNumber,
        capacity: bus.capacity,
        model: {
          brand: bus.model?.brand || '',
          model: bus.model?.model || '',
          year: bus.model?.year || new Date().getFullYear(),
          color: bus.model?.color || ''
        },
        driver: {
          name: bus.driver.name,
          phone: bus.driver.phone,
          licenseNumber: bus.driver.licenseNumber,
          licenseExpiry: new Date(bus.driver.licenseExpiry).toISOString().split('T')[0],
          experience: bus.driver.experience || 0,
          idNumber: bus.driver.idNumber || ''
        },
        supervisor: {
          name: bus.supervisor?.name || '',
          phone: bus.supervisor?.phone || '',
          idNumber: bus.supervisor?.idNumber || ''
        },
        features: bus.features || [],
        routes: bus.routes || [],
        pricing: {
          costPerKM: bus.pricing.costPerKM,
          dailyRate: bus.pricing.dailyRate,
          fuelConsumption: bus.pricing.fuelConsumption
        },
        maintenance: {
          mileage: bus.maintenance.mileage,
          serviceInterval: bus.maintenance.serviceInterval,
          notes: bus.maintenance.notes || ''
        },
        status: bus.status,
        isActive: bus.isActive
      });
    }
  }, [isEditing, bus]);

  // Create/Update mutations
  const createMutation = useMutation({
    mutationFn: (data: Partial<Bus>) => apiService.createBus(data),
    onSuccess: (newBus) => {
      queryClient.invalidateQueries({ queryKey: ['buses'] });
      onSuccess?.(newBus);
      onClose();
      resetForm();
    },
    onError: (error: any) => {
      console.error('Create bus error:', error);
      setErrors({ submit: 'حدث خطأ أثناء إنشاء الحافلة' });
    }
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Bus>) => apiService.updateBus(bus!._id, data),
    onSuccess: (updatedBus) => {
      queryClient.invalidateQueries({ queryKey: ['buses'] });
      onSuccess?.(updatedBus);
      onClose();
    },
    onError: (error: any) => {
      console.error('Update bus error:', error);
      setErrors({ submit: 'حدث خطأ أثناء تحديث الحافلة' });
    }
  });

  const resetForm = () => {
    setFormData({
      busNumber: '',
      plateNumber: '',
      capacity: 45,
      model: {
        brand: '',
        model: '',
        year: new Date().getFullYear(),
        color: ''
      },
      driver: {
        name: '',
        phone: '',
        licenseNumber: '',
        licenseExpiry: '',
        experience: 0,
        idNumber: ''
      },
      supervisor: {
        name: '',
        phone: '',
        idNumber: ''
      },
      features: [],
      routes: [],
      pricing: {
        costPerKM: 2,
        dailyRate: 500,
        fuelConsumption: 8
      },
      maintenance: {
        mileage: 0,
        serviceInterval: 10000,
        notes: ''
      },
      status: 'available',
      isActive: true
    });
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.busNumber.trim()) {
      newErrors.busNumber = 'رقم الحافلة مطلوب';
    }

    if (!formData.plateNumber.trim()) {
      newErrors.plateNumber = 'رقم اللوحة مطلوب';
    }

    if (!formData.driver.name.trim()) {
      newErrors.driverName = 'اسم السائق مطلوب';
    }

    if (!formData.driver.phone.trim()) {
      newErrors.driverPhone = 'رقم جوال السائق مطلوب';
    }

    if (!formData.driver.licenseNumber.trim()) {
      newErrors.driverLicense = 'رقم رخصة السائق مطلوب';
    }

    if (!formData.driver.licenseExpiry) {
      newErrors.driverLicenseExpiry = 'تاريخ انتهاء الرخصة مطلوب';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Convert form data to match Bus type
    const busData = {
      ...formData,
      driver: {
        ...formData.driver,
        licenseExpiry: new Date(formData.driver.licenseExpiry)
      },
      features: formData.features as any,
      routes: formData.routes as any
    };

    if (isEditing) {
      updateMutation.mutate(busData);
    } else {
      createMutation.mutate(busData);
    }
  };

  const handleFeatureChange = (feature: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      features: checked 
        ? [...prev.features, feature]
        : prev.features.filter(f => f !== feature)
    }));
  };

  const handleRouteChange = (route: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      routes: checked 
        ? [...prev.routes, route]
        : prev.routes.filter(r => r !== route)
    }));
  };

  if (!isOpen) return null;

  const availableFeatures = [
    { value: 'AC', label: 'تكييف' },
    { value: 'WiFi', label: 'واي فاي' },
    { value: 'Toilet', label: 'دورة مياه' },
    { value: 'TV', label: 'تلفزيون' },
    { value: 'USB_Charging', label: 'شحن USB' },
    { value: 'Reclining_Seats', label: 'مقاعد قابلة للإمالة' },
    { value: 'Reading_Lights', label: 'إضاءة قراءة' }
  ];

  const availableRoutes = [
    { value: 'حائل-مكة', label: 'حائل - مكة المكرمة' },
    { value: 'حائل-المدينة', label: 'حائل - المدينة المنورة' },
    { value: 'مكة-المدينة', label: 'مكة المكرمة - المدينة المنورة' },
    { value: 'داخلي', label: 'داخلي' }
  ];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <TruckIcon className="w-6 h-6 text-primary-600 ml-3" />
            <h3 className="text-lg font-medium text-gray-900">
              {isEditing ? 'تعديل الحافلة' : 'إضافة حافلة جديدة'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <FormCard title="المعلومات الأساسية" icon={<TruckIcon className="w-5 h-5 text-primary-600" />}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField label="رقم الحافلة" required error={errors.busNumber}>
                  <Input
                    value={formData.busNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, busNumber: e.target.value }))}
                    placeholder="مثال: BUS001"
                  />
                </FormField>

                <FormField label="رقم اللوحة" required error={errors.plateNumber}>
                  <Input
                    value={formData.plateNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, plateNumber: e.target.value }))}
                    placeholder="مثال: أ ب ج 1234"
                  />
                </FormField>

                <FormField label="السعة (عدد المقاعد)" required>
                  <Input
                    type="number"
                    value={formData.capacity}
                    onChange={(e) => setFormData(prev => ({ ...prev, capacity: parseInt(e.target.value) || 0 }))}
                    min="10"
                    max="60"
                  />
                </FormField>

                <FormField label="الحالة">
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as any }))}
                    options={[
                      { value: 'available', label: 'متاح' },
                      { value: 'on-trip', label: 'في رحلة' },
                      { value: 'maintenance', label: 'صيانة' },
                      { value: 'out-of-service', label: 'خارج الخدمة' }
                    ]}
                  />
                </FormField>
              </div>

              <div className="mt-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="mr-2 text-sm text-gray-700">الحافلة نشطة</span>
                </label>
              </div>
            </FormCard>

            {/* Vehicle Model */}
            <FormCard title="معلومات المركبة" icon={<TruckIcon className="w-5 h-5 text-primary-600" />}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField label="الماركة">
                  <Input
                    value={formData.model.brand}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      model: { ...prev.model, brand: e.target.value }
                    }))}
                    placeholder="مثال: مرسيدس"
                  />
                </FormField>

                <FormField label="الموديل">
                  <Input
                    value={formData.model.model}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      model: { ...prev.model, model: e.target.value }
                    }))}
                    placeholder="مثال: سبرينتر"
                  />
                </FormField>

                <FormField label="سنة الصنع">
                  <Input
                    type="number"
                    value={formData.model.year}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      model: { ...prev.model, year: parseInt(e.target.value) || new Date().getFullYear() }
                    }))}
                    min="2000"
                    max={new Date().getFullYear() + 1}
                  />
                </FormField>

                <FormField label="اللون">
                  <Input
                    value={formData.model.color}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      model: { ...prev.model, color: e.target.value }
                    }))}
                    placeholder="مثال: أبيض"
                  />
                </FormField>
              </div>
            </FormCard>

            {/* Driver Information */}
            <FormCard title="معلومات السائق" icon={<TruckIcon className="w-5 h-5 text-primary-600" />}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField label="اسم السائق" required error={errors.driverName}>
                  <Input
                    value={formData.driver.name}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      driver: { ...prev.driver, name: e.target.value }
                    }))}
                    placeholder="الاسم الكامل"
                  />
                </FormField>

                <FormField label="رقم الجوال" required error={errors.driverPhone}>
                  <Input
                    value={formData.driver.phone}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      driver: { ...prev.driver, phone: e.target.value }
                    }))}
                    placeholder="05xxxxxxxx"
                  />
                </FormField>

                <FormField label="رقم الرخصة" required error={errors.driverLicense}>
                  <Input
                    value={formData.driver.licenseNumber}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      driver: { ...prev.driver, licenseNumber: e.target.value }
                    }))}
                    placeholder="رقم رخصة القيادة"
                  />
                </FormField>

                <FormField error={errors.driverLicenseExpiry}>
                  <DateInput
                    label={
                      <>
                        تاريخ انتهاء الرخصة <span className="text-red-500">*</span>
                      </>
                    }
                    compactToggle
                    error={!!errors.driverLicenseExpiry}
                    value={formData.driver.licenseExpiry}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      driver: { ...prev.driver, licenseExpiry: e.target.value },
                    }))}
                  />
                </FormField>

                <FormField label="سنوات الخبرة">
                  <Input
                    type="number"
                    value={formData.driver.experience}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      driver: { ...prev.driver, experience: parseInt(e.target.value) || 0 }
                    }))}
                    min="0"
                  />
                </FormField>

                <FormField label="رقم الهوية">
                  <Input
                    value={formData.driver.idNumber}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      driver: { ...prev.driver, idNumber: e.target.value }
                    }))}
                    placeholder="رقم الهوية الوطنية"
                  />
                </FormField>
              </div>
            </FormCard>

            {/* Supervisor Information */}
            <FormCard title="معلومات المشرف (اختياري)" icon={<TruckIcon className="w-5 h-5 text-primary-600" />}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <FormField label="اسم المشرف">
                  <Input
                    value={formData.supervisor.name}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      supervisor: { ...prev.supervisor, name: e.target.value }
                    }))}
                    placeholder="الاسم الكامل"
                  />
                </FormField>

                <FormField label="رقم الجوال">
                  <Input
                    value={formData.supervisor.phone}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      supervisor: { ...prev.supervisor, phone: e.target.value }
                    }))}
                    placeholder="05xxxxxxxx"
                  />
                </FormField>

                <FormField label="رقم الهوية">
                  <Input
                    value={formData.supervisor.idNumber}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      supervisor: { ...prev.supervisor, idNumber: e.target.value }
                    }))}
                    placeholder="رقم الهوية الوطنية"
                  />
                </FormField>
              </div>
            </FormCard>

            {/* Features */}
            <FormCard title="المميزات" icon={<TruckIcon className="w-5 h-5 text-primary-600" />}>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {availableFeatures.map((feature) => (
                  <label key={feature.value} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.features.includes(feature.value)}
                      onChange={(e) => handleFeatureChange(feature.value, e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="mr-2 text-sm text-gray-700">{feature.label}</span>
                  </label>
                ))}
              </div>
            </FormCard>

            {/* Routes */}
            <FormCard title="المسارات المتاحة" icon={<TruckIcon className="w-5 h-5 text-primary-600" />}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {availableRoutes.map((route) => (
                  <label key={route.value} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.routes.includes(route.value)}
                      onChange={(e) => handleRouteChange(route.value, e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="mr-2 text-sm text-gray-700">{route.label}</span>
                  </label>
                ))}
              </div>
            </FormCard>

            {/* Pricing */}
            <FormCard title="التسعير" icon={<TruckIcon className="w-5 h-5 text-primary-600" />}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <FormField label="التكلفة لكل كيلومتر (ر.س)">
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.pricing.costPerKM}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      pricing: { ...prev.pricing, costPerKM: parseFloat(e.target.value) || 0 }
                    }))}
                  />
                </FormField>

                <FormField label="الأجرة اليومية (ر.س)">
                  <Input
                    type="number"
                    value={formData.pricing.dailyRate}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      pricing: { ...prev.pricing, dailyRate: parseInt(e.target.value) || 0 }
                    }))}
                  />
                </FormField>

                <FormField label="استهلاك الوقود (لتر/100كم)">
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.pricing.fuelConsumption}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      pricing: { ...prev.pricing, fuelConsumption: parseFloat(e.target.value) || 0 }
                    }))}
                  />
                </FormField>
              </div>
            </FormCard>

            {/* Maintenance */}
            <FormCard title="معلومات الصيانة" icon={<TruckIcon className="w-5 h-5 text-primary-600" />}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField label="الكيلومترات الحالية">
                  <Input
                    type="number"
                    value={formData.maintenance.mileage}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      maintenance: { ...prev.maintenance, mileage: parseInt(e.target.value) || 0 }
                    }))}
                  />
                </FormField>

                <FormField label="فترة الصيانة (كم)">
                  <Input
                    type="number"
                    value={formData.maintenance.serviceInterval}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      maintenance: { ...prev.maintenance, serviceInterval: parseInt(e.target.value) || 10000 }
                    }))}
                  />
                </FormField>
              </div>

              <div className="mt-4">
                <FormField label="ملاحظات الصيانة">
                  <Textarea
                    value={formData.maintenance.notes}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      maintenance: { ...prev.maintenance, notes: e.target.value }
                    }))}
                    placeholder="ملاحظات حول حالة الحافلة أو الصيانة المطلوبة"
                    rows={3}
                  />
                </FormField>
              </div>
            </FormCard>
          </div>

          {/* Error Message */}
          {errors.submit && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-600 text-sm">{errors.submit}</p>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 space-x-reverse mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="btn-primary"
            >
              {createMutation.isPending || updateMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2"></div>
                  {isEditing ? 'جاري التحديث...' : 'جاري الحفظ...'}
                </>
              ) : (
                isEditing ? 'تحديث الحافلة' : 'إضافة الحافلة'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BusForm; 